import i18n from "@/i18n"
import { getCsrfToken } from "@/lib/workspace-api"
import type { VoiceSelection } from "@/lib/voices-api"
import type { GenerationJobRow } from "@/types/workspace"
import type { TimelineAudioAsset, TimelineDocument, TimelineVersionOption } from "./types"

function jsonHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-CSRF-Token": getCsrfToken(),
  }
}

type OkResponse = { ok?: boolean; error?: string; conflict?: boolean }

function raise(data: OkResponse, status: number): never {
  throw new Error(data.error ?? i18n.t("shot:apiErrors.saveFailed", { status }))
}

/** Конфликт: активная версия на сервере уже сменилась — не перезаписывать таймлайн старым doc. */
export class StaleTimelineConflictError extends Error {
  constructor() {
    super("stale_active_video_version")
    this.name = "StaleTimelineConflictError"
  }
}

/** Дебаунс-сохранение документа таймлайна; сервер возвращает нормализованную версию. */
export async function patchShotTimeline(
  projectId: number,
  shotId: number,
  timeline: TimelineDocument,
  expectedActiveVideoVersionId?: number | null,
): Promise<TimelineDocument | null> {
  const body: Record<string, unknown> = { timeline }
  if (expectedActiveVideoVersionId != null) {
    body.expected_active_video_version_id = expectedActiveVideoVersionId
  }
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/timeline`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as OkResponse & { timeline?: TimelineDocument }
  if (res.status === 409 || data.conflict) {
    throw new StaleTimelineConflictError()
  }
  if (!res.ok || !data.ok) raise(data, res.status)
  return data.timeline && data.timeline.tracks ? data.timeline : null
}

/** Переименование версии шота; сервер возвращает обновлённую опцию версии. */
export async function renameShotMediaVersion(
  projectId: number,
  shotId: number,
  versionId: number,
  name: string,
): Promise<TimelineVersionOption> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/media_versions/${versionId}/name`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  })
  const data = (await res.json().catch(() => ({}))) as OkResponse & { version?: TimelineVersionOption }
  if (!res.ok || !data.ok || !data.version) raise(data, res.status)
  return data.version
}

export async function renderShotTimeline(projectId: number, shotId: number): Promise<GenerationJobRow> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/timeline/render`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
  })
  const data = (await res.json().catch(() => ({}))) as OkResponse & { job?: GenerationJobRow }
  if (!res.ok || !data.ok || !data.job) raise(data, res.status)
  return data.job
}

/** Запуск lipsync выделенной пары клипов (видео + аудио) через sync.so. */
export async function postShotTimelineLipsync(
  projectId: number,
  shotId: number,
  params: { videoClipId: string; audioClipId: string },
): Promise<GenerationJobRow> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/timeline/lipsync`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({ video_clip_id: params.videoClipId, audio_clip_id: params.audioClipId }),
  })
  const data = (await res.json().catch(() => ({}))) as OkResponse & { job?: GenerationJobRow }
  if (!res.ok || !data.ok || !data.job) raise(data, res.status)
  return data.job
}

export async function createShotAudioAsset(
  projectId: number,
  shotId: number,
  params: {
    file: Blob
    filename: string
    kind: "upload" | "recording"
    name?: string
    /** Голос замены (STS); null/undefined — запись ложится как есть. */
    voice?: VoiceSelection | null
  },
): Promise<TimelineAudioAsset> {
  const form = new FormData()
  form.append("file", params.file, params.filename)
  form.append("kind", params.kind)
  if (params.name) form.append("name", params.name)
  if (params.voice) {
    form.append("voice_change", "true")
    // Голос героя уходит как voice_id, библиотечный — парой {provider_kind, provider_voice_id}
    // (сервер проверяет её по whitelist библиотеки компании).
    if (params.voice.kind === "voice") {
      form.append("voice_id", String(params.voice.voice_id))
    } else {
      form.append("provider_kind", params.voice.provider_kind)
      form.append("provider_voice_id", params.voice.provider_voice_id)
    }
  }
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/audio_assets`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
    body: form,
  })
  const data = (await res.json().catch(() => ({}))) as OkResponse & { asset?: TimelineAudioAsset }
  if (!res.ok || !data.ok || !data.asset) raise(data, res.status)
  return data.asset
}

export async function createShotAudioAssetTts(
  projectId: number,
  shotId: number,
  params: { text: string; voice: VoiceSelection },
): Promise<TimelineAudioAsset> {
  // Голос героя уходит как voice_id, библиотечный — парой {provider_kind, provider_voice_id}
  // (сервер проверяет её по whitelist библиотеки компании).
  const voiceBody =
    params.voice.kind === "voice"
      ? { voice_id: params.voice.voice_id }
      : { provider_kind: params.voice.provider_kind, provider_voice_id: params.voice.provider_voice_id }
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/audio_assets/tts`, {
    method: "POST",
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify({ text: params.text, ...voiceBody }),
  })
  const data = (await res.json().catch(() => ({}))) as OkResponse & { asset?: TimelineAudioAsset }
  if (!res.ok || !data.ok || !data.asset) raise(data, res.status)
  return data.asset
}

/** Ассета больше нет: сервер удалил failed TTS/STS-ассет, ошибка живёт в панели «Задачи». */
export class AssetGoneError extends Error {
  constructor() {
    super("asset_gone")
    this.name = "AssetGoneError"
  }
}

export async function fetchShotAudioAsset(
  projectId: number,
  shotId: number,
  assetId: number,
): Promise<TimelineAudioAsset> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/audio_assets/${assetId}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  if (res.status === 404) throw new AssetGoneError()
  const data = (await res.json().catch(() => ({}))) as OkResponse & { asset?: TimelineAudioAsset }
  if (!res.ok || !data.ok || !data.asset) raise(data, res.status)
  return data.asset
}

export async function destroyShotAudioAsset(
  projectId: number,
  shotId: number,
  assetId: number,
): Promise<TimelineDocument | null> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/audio_assets/${assetId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
  })
  const data = (await res.json().catch(() => ({}))) as OkResponse & { timeline?: TimelineDocument }
  if (!res.ok || !data.ok) raise(data, res.status)
  return data.timeline && data.timeline.tracks ? data.timeline : null
}
